import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, concatMap, from, map, Observable, of, switchMap, tap, toArray } from 'rxjs';

import { ApiPaginatedResponseDto } from '@app/api/common-dtos';
import {
  ImConferenceDto,
  ImConferenceMemberDto,
  ImGroupDto,
  ImGroupMemberDto,
  ImGroupMemberTypeEnum,
} from '@app/api/im/dtos';
import { ImConferenceApiService } from '@app/api/im/im-conference-api.service';
import { ImGroupApiService } from '@app/api/im/im-group-api.service';
import { loadAllPaginatedItems } from '@app/shared/utils/paginated-request.utils';

import { ImGroupStateActions } from './im-group.state.actions';

export interface ImGroupStateItem {
  groupId: string;
  imGroup: ImGroupDto;
  imMembers: ImGroupMemberDto[];
  conference: ImConferenceDto | null;
  conferenceMemberMap: Record<string, ImConferenceMemberDto>;
}

type ImGroupStateModel = ImGroupStateItem[];

function sortMembersPredicate(a: ImGroupMemberDto, b: ImGroupMemberDto): number {
  const aName = a.name || a.memberName;
  const bName = b.name || b.memberName;

  return aName >= bName ? 1 : -1;
}

function sortStateItemsPredicate(a: ImGroupStateItem, b: ImGroupStateItem): number {
  return a.imGroup.name >= b.imGroup.name ? 1 : -1;
}

@State<ImGroupStateModel>({
  name: 'imGroups',
  defaults: [],
})
@Injectable()
export class ImGroupState {
  @Selector()
  public static groups(state: ImGroupStateModel): ImGroupStateItem[] {
    return state;
  }

  constructor(
    private readonly imGroupApi: ImGroupApiService,
    private readonly imConferenceApi: ImConferenceApiService,
  ) {}

  @Action(ImGroupStateActions.Load, { cancelUncompleted: true })
  public load({ setState }: StateContext<ImGroupStateModel>): Observable<ImGroupStateItem[]> {
    return loadAllPaginatedItems((request) =>
      this.imGroupApi.getGroups(request).pipe(concatMap((response) => this.enrichGroupsBatch(response))),
    ).pipe(tap((groups) => setState(groups)));
  }

  @Action(ImGroupStateActions.ReloadGroup)
  public reloadGroup(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.ReloadGroup,
  ): Observable<ImGroupStateItem | null> {
    return this.imGroupApi.getGroup(groupId).pipe(
      switchMap((group) => (group ? this.enrichGroup(group) : of(null))),
      tap((group) => {
        if (!group) {
          return;
        }

        const state = [...getState()];
        const groupIndex = state.findIndex((i) => i.groupId === groupId);

        if (groupIndex >= 0) {
          state.splice(groupIndex, 1, group);
        } else {
          state.push(group);
        }

        state.sort(sortStateItemsPredicate);
        setState(state);
      }),
    );
  }

  @Action(ImGroupStateActions.LoadGroupIfNotLoaded)
  public loadGroupIfNotLoaded(
    { getState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.LoadGroupIfNotLoaded,
  ): void {
    const state = getState();

    if (!state.find((i) => i.groupId === groupId)) {
      dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    }
  }

  @Action(ImGroupStateActions.DeleteGroup)
  public deleteGroup(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.DeleteGroup,
  ): void {
    const state = [...getState()];
    const index = state.findIndex((i) => i.groupId === groupId);

    if (index >= 0) {
      state.splice(index, 1);
      setState(state);
    }
  }

  @Action(ImGroupStateActions.ReloadGroupMember)
  public reloadGroupMember(
    { getState, setState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId, memberId }: ImGroupStateActions.ReloadGroupMember,
  ): Observable<unknown> {
    const hasItem = getState().findIndex((i) => i.groupId === groupId) >= 0;

    if (!hasItem) {
      return dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    } else {
      return this.imGroupApi.getGroupMember(groupId, memberId).pipe(
        tap((member) => {
          if (!member) {
            return;
          }

          const state = [...getState()];
          const itemIndex = state.findIndex((i) => i.groupId === groupId);

          if (itemIndex >= 0) {
            const group = state[itemIndex];
            const members = [...group.imMembers];
            const memberIndex = members.findIndex(
              (i) => i.memberID === member.memberID && i.memberType === member.memberType,
            );

            if (memberIndex >= 0) {
              members.splice(memberIndex, 1, member);
            } else {
              members.push(member);
            }

            state.splice(itemIndex, 1, {
              ...group,
              imMembers: members.sort(sortMembersPredicate),
            });

            setState(state);
          } else {
            dispatch(new ImGroupStateActions.ReloadGroup(groupId));
          }
        }),
      );
    }
  }

  @Action(ImGroupStateActions.DeleteGroupMember)
  public deleteGroupMember(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId, memberId, memberType }: ImGroupStateActions.DeleteGroupMember,
  ): void {
    const state = [...getState()];
    const groupIndex = state.findIndex((i) => i.groupId === groupId);

    if (groupIndex >= 0) {
      const group = state[groupIndex];
      const members = [...group.imMembers];
      const memberIndex = members.findIndex((i) => i.memberID === memberId && i.memberType === memberType);

      if (memberIndex >= 0) {
        members.splice(memberIndex, 1);
      }

      state.splice(groupIndex, 1, {
        ...group,
        imMembers: members,
      });

      setState(state);
    }
  }

  @Action(ImGroupStateActions.LoadConference)
  public loadConference(
    { getState, setState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.LoadConference,
  ): Observable<unknown> {
    const groupNotLoaded = getState().findIndex((i) => i.groupId === groupId) < 0;

    if (groupNotLoaded) {
      return dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    } else {
      return this.imConferenceApi.getConference(groupId).pipe(
        tap((conference) => {
          if (!conference) {
            return;
          }

          const state = [...getState()];
          const groupIndex = state.findIndex((i) => i.groupId === groupId);

          if (groupIndex >= 0) {
            const group = state[groupIndex];
            state.splice(groupIndex, 1, {
              ...group,
              conference,
            });

            setState(state);
          } else {
            dispatch(new ImGroupStateActions.ReloadGroup(groupId));
          }
        }),
      );
    }
  }

  @Action(ImGroupStateActions.DeleteConference)
  public deleteConference(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.DeleteConference,
  ): void {
    const state = [...getState()];
    const groupIndex = state.findIndex((i) => i.groupId === groupId);

    if (groupIndex >= 0) {
      const group = state[groupIndex];
      state.splice(groupIndex, 1, {
        ...group,
        conference: null,
        conferenceMemberMap: {},
      });

      setState(state);
    }
  }

  @Action(ImGroupStateActions.UpdateConference)
  public updateConference(
    { getState, setState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId, mode }: ImGroupStateActions.UpdateConference,
  ): void {
    const state = [...getState()];
    const groupIndex = state.findIndex((i) => i.groupId === groupId);

    if (groupIndex >= 0) {
      const group = state[groupIndex];

      if (group.conference) {
        state.splice(groupIndex, 1, {
          ...group,
          conference: {
            ...group.conference,
            mode,
          },
        });

        setState(state);
      } else {
        dispatch(new ImGroupStateActions.LoadConference(groupId));
      }
    } else {
      dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    }
  }

  @Action(ImGroupStateActions.ReloadConferenceMembers)
  public reloadConferenceMembers(
    { getState, setState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId }: ImGroupStateActions.ReloadConferenceMembers,
  ): Observable<unknown> {
    const itemNotFound = getState().findIndex((i) => i.groupId === groupId) < 0;

    if (itemNotFound) {
      return dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    } else {
      return this.imConferenceApi.getConferenceMembers(groupId).pipe(
        tap((members) => {
          const state = [...getState()];
          const index = state.findIndex((i) => i.groupId === groupId);

          if (index >= 0) {
            const item = state[index];
            state.splice(index, 1, {
              ...item,
              conferenceMemberMap: members.reduce((total, member) => ({ ...total, [member.memberID]: member }), {}),
            });
            setState(state);
          } else {
            dispatch(new ImGroupStateActions.ReloadGroup(groupId));
          }
        }),
      );
    }
  }

  @Action(ImGroupStateActions.UpsertConferenceMember)
  addConferenceMembers(
    { getState, setState, dispatch }: StateContext<ImGroupStateModel>,
    { groupId, member }: ImGroupStateActions.UpsertConferenceMember,
  ): void {
    const state = [...getState()];
    const index = state.findIndex((i) => i.groupId === groupId);

    if (index >= 0) {
      const item = state[index];

      state.splice(index, 1, {
        ...item,
        conferenceMemberMap: {
          ...item.conferenceMemberMap,
          [member.memberID]: member,
        },
      });

      setState(state);
    } else {
      dispatch(new ImGroupStateActions.ReloadGroup(groupId));
    }
  }

  @Action(ImGroupStateActions.DeleteConferenceMember)
  deleteConferenceMembers(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { groupId, member }: ImGroupStateActions.DeleteConferenceMember,
  ): void {
    const state = [...getState()];
    const index = state.findIndex((i) => i.groupId === groupId);

    if (index >= 0) {
      const item = state[index];
      const conferenceMemberMap = { ...item.conferenceMemberMap };
      delete conferenceMemberMap[member.memberID];

      state.splice(index, 1, {
        ...item,
        conferenceMemberMap,
      });

      setState(state);
    }
  }

  @Action(ImGroupStateActions.HandleOnlineStatusChanged)
  handleOnlineStatusChanged(
    { getState, setState }: StateContext<ImGroupStateModel>,
    { message }: ImGroupStateActions.HandleOnlineStatusChanged,
  ): void {
    if (!message.user?.id && !message.device?.id) {
      return;
    }

    const original = getState();
    const state = [...original];
    let stateChanged = false;

    original.forEach((item, itemIndex) => {
      const imMembers = [...item.imMembers];
      let membersChanged = false;

      item.imMembers.forEach((member, memberIndex) => {
        if (
          message.user?.id &&
          member.memberType === ImGroupMemberTypeEnum.User &&
          member.memberID === message.user.id
        ) {
          imMembers.splice(memberIndex, 1, {
            ...member,
            status: message.status,
          });

          membersChanged = true;
        }

        if (
          message.device?.id &&
          member.memberType === ImGroupMemberTypeEnum.PU &&
          member.memberID === message.device.id
        ) {
          imMembers.splice(memberIndex, 1, {
            ...member,
            status: message.status,
          });

          membersChanged = true;
        }
      });

      if (membersChanged) {
        state.splice(itemIndex, 1, {
          ...item,
          imMembers,
        });

        stateChanged = true;
      }
    });

    if (stateChanged) {
      setState(state);
    }
  }

  protected enrichGroupsBatch(
    response: ApiPaginatedResponseDto<ImGroupDto>,
  ): Observable<ApiPaginatedResponseDto<ImGroupStateItem>> {
    if (!response.data?.length) {
      return of({
        ...response,
        data: [],
      });
    }

    return from(response.data).pipe(
      concatMap((group) => this.enrichGroup(group)),
      toArray(),
      map((data) => ({
        ...response,
        data: data.sort(sortStateItemsPredicate),
      })),
    );
  }

  protected enrichGroup(group: ImGroupDto): Observable<ImGroupStateItem> {
    const loadMembers = () =>
      loadAllPaginatedItems(
        (request) => this.imGroupApi.getGroupMembers(group.id, { ...request, needStatus: true }),
        1000,
      ).pipe(catchError(() => of([])));

    const loadConference = () => this.imConferenceApi.getConference(group.id).pipe(catchError(() => of(null)));

    const loadConferenceMembers = (hasConference: boolean): Observable<Record<string, ImConferenceMemberDto>> =>
      hasConference
        ? this.imConferenceApi.getConferenceMembers(group.id).pipe(
            catchError(() => of([])),
            map((members) =>
              members.reduce(
                (total, member) => ({ ...total, [member.memberID]: member }),
                {} as Record<string, ImConferenceMemberDto>,
              ),
            ),
          )
        : of({});

    return loadMembers().pipe(
      switchMap((imMembers) =>
        loadConference().pipe(
          switchMap((conference) =>
            loadConferenceMembers(!!conference).pipe(
              map(
                (conferenceMemberMap): ImGroupStateItem => ({
                  groupId: group.id,
                  imGroup: group,
                  imMembers,
                  conference,
                  conferenceMemberMap,
                }),
              ),
            ),
          ),
        ),
      ),
    );
  }
}
